import { useMemo, type ReactElement, type ReactNode } from 'react'
import { parseAsString, useQueryState } from 'nuqs'
import { isEmpty } from '@terros/common'
import { Select, Stack } from '@mantine/core'
import { useConversationShareable, useSkillShareCategories, useTranslation } from '@front/model'
import { NotReady, Page } from '@components'
import { ConvoListItem } from '../conversations/ConvoListItem'
import { createFileRoute } from '@tanstack/react-router'

function SkillShare(): ReactElement {
  const { t } = useTranslation()
  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useSkillShareCategories()
  const [categoryCode, setCategoryCode] = useQueryState('category', parseAsString)

  const selectedCode = useMemo(() => {
    if (categoryCode) return categoryCode
    if (isEmpty(categories)) return undefined
    return categories[0].value
  }, [categoryCode, categories])

  const selectedLabel = useMemo(
    () => categories?.find((c) => c.value === selectedCode)?.label,
    [categories, selectedCode]
  )

  const renderContent = (): ReactNode => {
    if (categoriesLoading) {
      return <NotReady isLoading inline id='Skill Share' />
    }

    if (categoriesError) {
      return <NotReady error={categoriesError} inline id='Skill Share' />
    }

    if (!selectedLabel) {
      return <Page.Hero icon='local_library' title={t('No categories available')} />
    }

    return <SkillShareConversationList category={selectedLabel} />
  }

  return (
    <Page>
      <Page.Heading title={t('Skill Share')} back />
      <Page.FunctionBar>
        <Select
          placeholder={t('Select a category')}
          data={categories ?? []}
          value={selectedCode ?? null}
          onChange={(value) => setCategoryCode(value)}
          disabled={categoriesLoading}
          allowDeselect={false}
        />
      </Page.FunctionBar>

      <Page.Content scrollArea>{renderContent()}</Page.Content>
    </Page>
  )
}

type SkillShareConversationListProps = {
  category: string
}

const SkillShareConversationList = ({ category }: SkillShareConversationListProps): ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, error } = useConversationShareable(category)

  if (isLoading || error) {
    return <NotReady isLoading={isLoading} error={error} inline data={data} id='Skill Share' />
  }

  if (isEmpty(data)) {
    return <Page.Hero icon='local_library' title={t('No conversations found')} />
  }

  return (
    <Stack>
      {data.map((convo) => (
        <ConvoListItem key={convo.convoId} convo={convo} categoryTitle={category} />
      ))}
    </Stack>
  )
}

export const Route = createFileRoute('/conversations_/skillShare/')({
  component: SkillShare,
})
